import { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import AIAssistant from './AIAssistant';
import { BroadcastBanner } from './BroadcastBanner';
import { OnboardingTour } from './OnboardingTour';
import { useI18n } from '../lib/i18n';
import { NAV, ITEMS, MODULE_OF } from './shell/navConfig';
import { SidebarNav } from './shell/SidebarNav';
import { TopBar } from './shell/TopBar';
import { SearchPalette } from './shell/SearchPalette';
import { MobileBottomNav } from './shell/MobileBottomNav';

export default function Shell({ children }) {
  const { user, logout } = useAuth();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [q, setQ] = useState('');
  const [aiOpen, setAiOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const { lang, setLang, t } = useI18n();
  const { data: enabledMods } = useQuery({ queryKey: ['modules-enabled'], queryFn: () => api.get('/modules/enabled').then((r) => r.data.enabled), enabled: !!user, staleTime: 60000 });
  const items = useMemo(() => (NAV[user?.role] || ['dashboard'])
    .filter((k) => !MODULE_OF[k] || !enabledMods || enabledMods.includes(MODULE_OF[k]))
    .map((k) => ITEMS[k]), [user, enabledMods]);
  const hasChat = (NAV[user?.role] || []).includes('chat') && (!enabledMods || enabledMods.includes('chat'));
  const { data: chatUnread } = useQuery({
    queryKey: ['chat-unread-total'],
    queryFn: () => api.get('/chat/unread-count').then((r) => r.data),
    refetchInterval: 15000, enabled: hasChat,
  });
  const { data: notifs } = useQuery({ queryKey: ['notifs'], queryFn: () => api.get('/notifications').then((r) => r.data), refetchInterval: 30000 });
  const { data: aiAvail } = useQuery({ queryKey: ['ai-avail'], queryFn: () => api.get('/ai/availability').then((r) => r.data) });
  const { data: search } = useQuery({ queryKey: ['search', q], queryFn: () => api.get('/search', { params: { q } }).then((r) => r.data), enabled: q.length > 1 });
  const unread = (notifs || []).filter((n) => !n.read).length;

  useEffect(() => {
    const h = (e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); setCmdOpen((o) => !o); } };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 noise-bg">
      {mobileNav && <div className="fixed inset-0 z-40 bg-slate-900/40 md:hidden" onClick={() => setMobileNav(false)} data-testid="mobile-nav-backdrop" />}
      <SidebarNav user={user} items={items} chatUnread={chatUnread} mobileNav={mobileNav} setMobileNav={setMobileNav} t={t} logout={logout} />
      <div className="md:ps-64">
        <TopBar t={t} lang={lang} setLang={setLang} setCmdOpen={setCmdOpen} mobileNav={mobileNav} setMobileNav={setMobileNav}
          aiAvail={aiAvail} setAiOpen={setAiOpen} notifs={notifs} unread={unread} />
        <BroadcastBanner />
        <main className="p-4 sm:p-8 max-w-[1400px] pb-24 md:pb-8">{children}</main>
      </div>
      <MobileBottomNav items={items} chatUnread={chatUnread} t={t} setMobileNav={setMobileNav} />
      <SearchPalette open={cmdOpen} setOpen={setCmdOpen} q={q} setQ={setQ} search={search} items={items} />
      <AIAssistant open={aiOpen} onClose={() => setAiOpen(false)} persona={aiAvail?.persona} />
      <OnboardingTour />
    </div>
  );
}
